import { createPortal } from 'react-dom';
import { Link } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn, useMediaQuery } from '@blimo/ui';
import { useHeaderSlot } from './header-slot.js';

/**
 * Title block of a page (replaces `PageHeader` of @blimo/ui in the portal): title 24 px, description 14 px muted,
 * page actions right-aligned next to the title (below it on phones).
 *
 * The breadcrumb of the page goes into the sticky header bar of the shell (md and up); on detail pages (`back`) its parent
 * link is the `nav` „Zurück“. Below md the header has no room for it – the back link sits above the title instead.
 *
 * @param {{
 *   title: import('react').ReactNode,
 *   compactTitle?: string,
 *   description?: import('react').ReactNode,
 *   actions?: import('react').ReactNode,
 *   back?: { to: string, params?: Record<string, string>, search?: any, label: string },
 *   className?: string,
 * }} props
 *   compactTitle: short title for the breadcrumb (defaults to `title` when it is a string)
 */
export function PageHeader({ title, compactTitle, description, actions, back, className }) {
  const slot = useHeaderSlot();
  const wide = useMediaQuery('(min-width: 768px)', true);
  const current = compactTitle ?? (typeof title === 'string' ? title : undefined);
  const inHeader = Boolean(slot) && wide;

  const breadcrumb = (
    <div className="flex min-w-0 items-center gap-1.5 text-sm">
      {back && (
        <>
          <nav aria-label="Zurück" className="min-w-0 shrink">
            <Link
              to={back.to}
              params={back.params}
              search={back.search}
              title={`Zurück zu ${back.label}`}
              className="block truncate text-muted-foreground transition-colors hover:text-foreground"
            >
              {back.label}
            </Link>
          </nav>
          <ChevronRight className="size-3.5 shrink-0 text-muted-foreground/70" aria-hidden="true" />
        </>
      )}
      {current && <span className="min-w-0 truncate font-medium">{current}</span>}
    </div>
  );

  return (
    <>
      {inHeader && createPortal(breadcrumb, slot)}
      <header data-slot="page-header" className={cn('flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between', className)}>
        <div className="flex min-w-0 flex-col gap-1">
          {back && !inHeader && (
            <nav aria-label="Zurück" className="-ml-1 mb-1">
              <Link
                to={back.to}
                params={back.params}
                search={back.search}
                title={`Zurück zu ${back.label}`}
                className="inline-flex min-h-8 max-w-full items-center gap-0.5 rounded-md pr-1.5 text-[0.8125rem] text-muted-foreground hover:text-foreground"
              >
                <ChevronLeft className="size-4 shrink-0" aria-hidden="true" />
                <span className="truncate">{back.label}</span>
              </Link>
            </nav>
          )}
          <h1 className="text-2xl font-semibold tracking-tight text-balance break-words">{title}</h1>
          {description && <div className="text-sm text-muted-foreground">{description}</div>}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2 print:hidden">{actions}</div>}
      </header>
    </>
  );
}
